<template>
	<div
		class="p-4 sm:p-6 lg:p-10 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-blue-100/50"
	>
		<div class="mb-6 sm:mb-10 text-center">
			<div class="flex justify-center mb-4 sm:mb-5">
				<div
					class="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center"
				>
					<i class="fas fa-shield-alt text-blue-600"></i>
				</div>
			</div>
			<h2
				class="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight px-4"
			>
				Customize Your Coverage
			</h2>
			<p
				class="mt-2 sm:mt-3 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 px-4"
			>
				Tailor your insurance policy by adjusting benefits and excesses
				to match your specific needs
			</p>
		</div>

		<div
			class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-10"
		>
			<!-- Card: Core Benefits -->
			<div
				class="bg-white rounded-xl shadow-md border border-blue-100 overflow-hidden"
			>
				<div
					class="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6"
				>
					<h3 class="text-lg sm:text-xl font-semibold text-white">
						Core Benefits
					</h3>
					<p class="text-blue-100 text-xs sm:text-sm mt-1">
						Essential coverage included in your product
					</p>
				</div>
				<div class="p-4 sm:p-6">
					<div class="space-y-3 sm:space-y-4">
						<div
							v-for="benefit in embeddedBenefits"
							:key="'core-' + benefit.benefitSystemRef"
							class="flex flex-col sm:flex-row sm:items-start sm:justify-between p-4 bg-blue-50/50 rounded-xl border border-blue-200/60 hover:bg-blue-50 transition gap-4"
						>
							<div class="flex-1 min-w-0">
								<div
									class="font-semibold text-sm sm:text-base text-slate-800"
								>
									{{ benefit.benefit_category_name }}
								</div>
								<p
									class="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2"
								>
									{{
										benefit.benefit_category_description ||
										'Core benefit included in the package'
									}}
								</p>
								<div
									class="text-xs sm:text-sm text-slate-700 mt-2 sm:mt-3"
								>
									Limit:
									<span
										class="font-bold text-blue-700 ml-1"
										>{{ displayCoreLimit(benefit) }}</span
									>
								</div>
							</div>
							<div class="flex-shrink-0 w-full sm:w-auto sm:ml-6">
								<template
									v-if="
										benefit.benefitLimit &&
										benefit.benefitLimit.isAdjustable
									"
								>
									<template
										v-if="
											editingCore[
												benefit.benefitSystemRef
											]
										"
									>
										<input
											type="number"
											class="w-full sm:w-32 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
											v-model.number="
												coreInputs[
													benefit.benefitSystemRef
												]
											"
											@input="
												updateCoreInput(
													benefit.benefitSystemRef,
												)
											"
										/>
										<div class="mt-3 flex gap-2 sm:gap-3">
											<button
												@click="
													saveQuoteBenefit(benefit)
												"
												class="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
											>
												Save
											</button>
											<button
												@click="cancelCoreEdit(benefit)"
												class="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300 transition"
											>
												Cancel
											</button>
										</div>
									</template>
									<template v-else>
										<button
											@click.prevent="
												startEditCoreBenefit(benefit)
											"
											class="w-full sm:w-auto px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition"
										>
											Edit Limit
										</button>
									</template>
								</template>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Card: Optional Benefits -->
			<div
				class="bg-white rounded-xl shadow-md border border-blue-100 overflow-hidden"
			>
				<div
					class="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6"
				>
					<h3 class="text-lg sm:text-xl font-semibold text-white">
						Optional Benefits
					</h3>
					<p class="text-blue-100 text-xs sm:text-sm mt-1">
						Add-on covers you can include
					</p>
				</div>
				<div class="p-4 sm:p-6">
					<div class="space-y-3 sm:space-y-4">
						<div
							v-for="benefit in optionalBenefits"
							:key="'opt-' + benefit.benefitSystemRef"
							class="flex flex-col sm:flex-row sm:items-start sm:justify-between p-4 bg-blue-50/50 rounded-xl border border-blue-200/60 hover:bg-blue-50 transition gap-4"
						>
							<div class="flex-1 min-w-0">
								<div
									class="font-semibold text-sm sm:text-base text-slate-800"
								>
									{{ benefit.benefit_category_name }}
								</div>
								<p
									class="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2"
								>
									{{
										benefit.benefit_category_description ||
										'Optional benefit you can add'
									}}
								</p>
								<div
									class="text-xs sm:text-sm text-slate-700 mt-2 sm:mt-3"
								>
									Limit:
									<span
										class="font-bold text-blue-700 ml-1"
										>{{ displayCoreLimit(benefit) }}</span
									>
								</div>
								<button
									v-if="
										!benefit.benefitLimit ||
										!benefit.benefitLimit.isAdjustable
									"
									@click="saveQuoteBenefit(benefit)"
									class="mt-3 sm:mt-4 w-full sm:w-auto px-5 py-2 text-xs sm:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition"
								>
									Add
								</button>
							</div>
							<div class="flex-shrink-0 w-full sm:w-auto sm:ml-6">
								<template
									v-if="
										benefit.benefitLimit &&
										benefit.benefitLimit.isAdjustable
									"
								>
									<template
										v-if="
											editingCore[
												benefit.benefitSystemRef
											]
										"
									>
										<input
											type="number"
											class="w-full sm:w-32 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
											v-model.number="
												coreInputs[
													benefit.benefitSystemRef
												]
											"
											@input="
												updateCoreInput(
													benefit.benefitSystemRef,
												)
											"
										/>
										<div class="mt-3 flex gap-2 sm:gap-3">
											<button
												@click="
													saveQuoteBenefit(benefit)
												"
												class="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
											>
												Save
											</button>
											<button
												@click="cancelCoreEdit(benefit)"
												class="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300 transition"
											>
												Cancel
											</button>
										</div>
									</template>
									<template v-else>
										<button
											@click.prevent="
												startEditCoreBenefit(benefit)
											"
											class="w-full sm:w-auto px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition"
										>
											Edit Limit
										</button>
									</template>
								</template>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Card: Liabilities -->
			<div
				class="bg-white rounded-xl shadow-md border border-blue-100 overflow-hidden"
			>
				<div
					class="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6"
				>
					<h3 class="text-lg sm:text-xl font-semibold text-white">
						Liabilities
					</h3>
					<p class="text-blue-100 text-xs sm:text-sm mt-1">
						Select and adjust liabilities
					</p>
				</div>
				<div class="p-4 sm:p-6">
					<div class="space-y-3 sm:space-y-4">
						<div
							v-for="liability in liabilities"
							:key="liability.name"
							class="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition gap-3 sm:gap-4"
						>
							<label
								class="flex-1 flex items-center gap-3 sm:gap-4 cursor-pointer"
							>
								<!-- <input
                  type="checkbox"
                  :checked="liabilityEnabled[liability.name]"
                  @change="onLiabilityCheckboxChange(liability)"
                  class="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                /> -->
								<div>
									<div
										class="font-medium text-sm sm:text-base text-slate-800"
									>
										{{ liability.name }}
									</div>
									<div
										class="text-xs sm:text-sm text-slate-600"
									>
										{{ liability.description }}
									</div>
								</div>
							</label>
							<div class="text-left sm:text-right">
								<div
									class="text-xs sm:text-sm font-medium text-slate-700 mb-2"
								>
									KES {{ liability.amount }}
								</div>
								<!-- <template v-if="editingLiability[liability.name]">
                  <input
                    type="number"
                    class="w-full sm:w-32 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    v-model.number="liabilityInputs[liability.name]"
                    @input="updateLiabilityInput(liability.name)"
                  />
                  <div class="mt-3 flex gap-2 sm:gap-3 sm:justify-end">
                    <button
                      @click="saveInlineLiability(liability)"
                      class="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      @click="cancelInlineLiability(liability)"
                      class="flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-medium bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </template>
                <template v-else>
                  <button
                    @click.prevent="liabilityEnabled[liability.name] ? startEditLiability(liability) : addAndEditLiability(liability)"
                    class="w-full sm:w-auto px-4 sm:px-5 py-2.5 text-xs sm:text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition"
                  >
                    {{ liabilityEnabled[liability.name] ? 'Edit' : 'Add & Edit' }}
                  </button>
                </template> -->
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Card: Excesses -->
			<div
				class="bg-white rounded-xl shadow-md border border-blue-100 overflow-hidden"
			>
				<div
					class="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 flex items-center gap-3"
				>
					<i class="fas fa-info-circle text-white"></i>
					<h3 class="text-lg sm:text-xl font-semibold text-white">
						Excesses
					</h3>
				</div>
				<div class="p-4 sm:p-6">
					<div
						v-if="excesses.options && excesses.options.length"
						class="space-y-3"
					>
						<p class="text-xs sm:text-sm text-green-800">
							<span class="font-bold text-black">N/B:</span>
							Excess is the amount you pay during an insurance
							claim.
						</p>
						<p class="text-xs sm:text-sm text-primary">
							Example: If excess = KSh 30,000 and loss = KSh
							100,000 → insurer pays 70,000.
						</p>
						<dl></dl>
						<p class="text-xs sm:text-sm">
							Select an option below:
						</p>
						<div
							v-for="e in excesses.options"
							:key="e.id"
							class="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 sm:py-4 px-4 sm:px-5 bg-slate-50 rounded-xl hover:bg-slate-100 transition gap-3"
						>
							<label
								class="flex items-center gap-3 sm:gap-4 cursor-pointer flex-1"
							>
								<input
									type="checkbox"
									:checked="excessEnabled[e.label]"
									@change="toggleExcessCheckbox(e)"
									class="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
								/>
								<span
									class="text-sm sm:text-base font-medium text-slate-700"
									>{{ e.label }}</span
								>
							</label>
							<span
								class="text-sm sm:text-base font-bold text-blue-600 pl-8 sm:pl-0"
								>{{ e.value }}%</span
							>
						</div>
					</div>

					<!-- No Excesses Message -->
					<div
						v-else
						class="p-6 sm:p-8 bg-green-50 border-2 border-green-200 rounded-xl text-center"
					>
						<div
							class="flex flex-col items-center gap-3 text-green-700"
						>
							<i class="fas fa-check-circle text-green-700"></i>
							<span class="text-base sm:text-lg font-semibold"
								>No excesses applied</span
							>
							<p class="text-xs sm:text-sm text-green-600">
								This cover has zero deductible — full
								protection!
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Adjusted Limits Summary -->
		<div
			class="bg-white rounded-xl shadow-md border border-blue-100 overflow-hidden mb-6 sm:mb-10"
		>
			<div class="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6">
				<h3 class="text-lg sm:text-xl font-semibold text-white">
					Adjusted Limits Summary
				</h3>
				<p class="text-blue-100 text-xs sm:text-sm mt-1">
					Summary of all selected and adjusted benefits, liabilities,
					and excesses
				</p>
			</div>
			<div class="p-4 sm:p-6 max-h-96 overflow-y-auto">
				<div
					v-if="!hasAnyAdjustments"
					class="text-center py-8 text-sm sm:text-base text-slate-500"
				>
					No adjustments made yet
				</div>

				<div v-else class="space-y-6 sm:space-y-8">
					<!-- Benefits -->
					<div v-if="selectedBenefits && selectedBenefits.length">
						<h4
							class="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4"
						>
							Benefits
						</h4>
						<div class="space-y-3">
							<div
								v-for="(b, index) in selectedBenefits"
								:key="'sum-benefit-' + index"
								class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 px-4 bg-blue-50 rounded-lg gap-3"
							>
								<span
									class="font-medium text-sm sm:text-base text-slate-700"
									>{{ b.benefit.benefit_category_name }}</span
								>
								<div
									class="flex items-center justify-between sm:justify-end gap-3 sm:gap-4"
								>
									<span
										class="font-bold text-sm sm:text-base text-blue-700"
										>{{ formatKES(b.limit) }}</span
									>
									<button
										@click="deleteBenefit(b.benefit)"
										class="text-red-600 hover:text-red-800 text-xs sm:text-sm font-medium whitespace-nowrap"
									>
										Remove
									</button>
								</div>
							</div>
						</div>
					</div>

					<!-- Liabilities -->
					<div
						v-if="selectedLiabilities && selectedLiabilities.length"
					>
						<h4
							class="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4"
						>
							Liabilities
						</h4>
						<div class="space-y-3">
							<div
								v-for="l in selectedLiabilities"
								:key="'sum-liab-' + l.liability.name"
								class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 px-4 bg-gray-50 rounded-lg gap-3"
							>
								<span
									class="font-medium text-sm sm:text-base text-slate-700"
									>{{ l.liability.name }}</span
								>
								<div
									class="flex items-center justify-between sm:justify-end gap-3 sm:gap-4"
								>
									<span
										class="font-bold text-sm sm:text-base text-blue-700"
										>{{ formatKES(l.limit) }}</span
									>
								</div>
							</div>
						</div>
					</div>

					<!-- Excesses -->
					<div v-if="selectedExcesses && selectedExcesses.length">
						<h4
							class="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4"
						>
							Excesses
						</h4>
						<div class="space-y-3">
							<div
								v-for="excess in selectedExcesses"
								:key="'sum-excess-' + excess.id"
								class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 px-4 bg-amber-50 rounded-lg gap-3"
							>
								<span
									class="font-medium text-sm sm:text-base text-slate-700"
									>{{ excess.label }}</span
								>
								<div
									class="flex items-center justify-between sm:justify-end gap-3 sm:gap-4"
								>
									<span
										class="font-bold text-sm sm:text-base text-amber-700"
										>{{ excess.value }}%</span
									>
									<button
										@click="deleteExcess(excess)"
										class="text-red-600 hover:text-red-800 text-xs sm:text-sm font-medium whitespace-nowrap"
									>
										Remove
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Action Buttons -->
		<div
			class="flex flex-col sm:flex-row gap-4 justify-center sm:justify-between"
		>
			<button
				@click="previous"
				class="order-2 sm:order-1 flex items-center justify-center px-8 py-2.5 text-base font-semibold text-blue-700 bg-white border-2 border-blue-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition"
			>
				<i class="fas fa-arrow-left mr-3"></i>
				Back
			</button>

			<button
				:disabled="isLoading"
				@click="save"
				class="order-1 sm:order-2 flex items-center justify-center px-10 py-2.5 text-base font-semibold text-white bg-blue-600 rounded-xl shadow-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition disabled:opacity-70"
			>
				<i
					v-if="isLoading"
					class="fas fa-spinner animate-spin -ml-1 mr-3 h-5 w-5 text-white"
				></i>
				<span v-if="isLoading">Getting quote...</span>
				<template v-else>
					<i class="fas fa-check mr-3"></i>
					Save & View Quote
				</template>
			</button>
		</div>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import BenefitModal from './benefitModal.vue';
import { GetCmQuotation } from '../../../api/insurance';
import Quotation from './Quotation.vue';

export default {
	name: 'BenefitsPage',
	components: {
		BenefitModal,
	},
	data() {
		return {
			isModalOpen: false,
			modalBenefit: null,
			isLoading: false,
			excessInputs: {},
			editingExcess: {},
			liabilityInputs: {},
			editingLiability: {},
			benefitInputs: {},
			editingOptional: {},
			excessEnabled: {},
			liabilityEnabled: {},

			mockExcesses: [
				{
					id: 'collision',
					name: 'Collision Excess',
					type: 'Per Claim',
					description: 'Amount you pay for collision damage claims',
					minAmount: 10000,
					maxAmount: 100000,
					defaultAmount: 25000,
					amount: 25000,
				},
			],
			coreInputs: {},
			editingCore: {},
			coreAdjustments: {},
		};
	},
	computed: {
		...mapState('product', ['selectedProduct']),
		...mapGetters('insurance/motorPrivate', [
			'coreBenefitLimits',
			'editingCoreBenefits',
			'editingOptionalBenefits',
		]),
		...mapGetters('benefits', [
			'selectedBenefits',
			'currentBenefit',
			'selectedLiabilities',
			'selectedExcesses',
		]),
		...mapGetters('quotation', ['selectQuotationData']),
		product() {
			return this.selectedProduct;
		},
		embeddedBenefits() {
			return (
				(this.product &&
					Array.isArray(this.product.productBenefits) &&
					this.product.productBenefits.filter(
						(benefit) =>
							benefit.benefitType === 'embedded' ||
							benefit.benefitType === 'inbuilt',
					)) ||
				[]
			);
		},
		optionalBenefits() {
			return (
				(this.product &&
					Array.isArray(this.product.productBenefits) &&
					this.product.productBenefits.filter(
						(benefit) => benefit.benefitType === 'optional',
					)) ||
				[]
			);
		},
		liabilities() {
			return (this.product && this.product.productLiability) || [];
		},

		excesses() {
			return (this.product && this.product.excessConfig) || [];
		},
		enabledExcesses(label) {
			if (!Array.isArray(this.excesses.options)) return [];
			return this.excesses.options.filter(
				(label) => this.excessEnabled[label],
			);
		},
		enabledLiabilities() {
			if (!Array.isArray(this.liabilities)) return [];
			return this.liabilities.filter(
				(liability) => this.liabilityEnabled[liability.name],
			);
		},
		showRightColumn() {
			return (
				this.selectedBenefits.length > 0 ||
				this.enabledExcesses.length > 0 ||
				this.enabledLiabilities.length > 0
			);
		},
		hasAnyAdjustments() {
			return (
				this.selectedBenefits.length > 0 ||
				this.enabledExcesses.length > 0 ||
				this.enabledLiabilities.length > 0 ||
				Object.keys(this.adjustedCoreLimits).length > 0
			);
		},
		adjustedCoreLimits() {
			// Return locally saved core adjustments (benefitSystemRef -> numeric limit)
			return this.coreAdjustments || {};
		},
	},
	methods: {
		startLoading() {
			this.isLoading = true;
		},
		stopLoading() {
			this.isLoading = false;
		},
		...mapActions('benefits', [
			'removeBenefit',
			'removeExcess',
			'updateSelectedBenefit',
			'setBenefitsData',
			'setLiabilityData',
			'updateSelectedLiabilityData',
			'setSelectedExcess',
			'updateSelectedExcess',
		]),
		...mapActions('insurance/motorPrivate', ['setInternalStep']),

		// ------------------------------
		// BENEFITS LOGIC
		// ------------------------------
		deleteBenefit(benefit) {
			return this.removeBenefit(benefit.benefitSystemRef);
		},

		handleConfirm(updatedBenefit) {
			console.log('Confirm received with benefit:', updatedBenefit);
			this.closeModal();
		},
		isBenefitSelected: function isBenefitSelected(benefitId) {
			if (!this.selectedBenefits || !Array.isArray(this.selectedBenefits))
				return false;
			return this.selectedBenefits.some(function (b) {
				return b.benefitSystemRef === benefitId;
			});
		},
		toggleOptionalBenefit(benefit) {
			const id = benefit.benefitSystemRef;
			const isSelected = this.isBenefitSelected(id);

			if (isSelected) {
				this.removeBenefit(id);
			} else {
				this.setBenefitsData(
					Object.assign({}, benefit, {
						params: {
							limit:
								benefit.benefitLimit &&
								benefit.benefitLimit.defaultLimit
									? Number(benefit.benefitLimit.defaultLimit)
									: null,
						},
					}),
				);
			}
		},

		onBenefitCheckboxChange(benefit) {
			if (!benefit || !benefit.benefitSystemRef) return;
			const id = benefit.benefitSystemRef;
			const selected = this.isBenefitSelected(id);
			if (selected) {
				this.removeBenefit(id);
				this.$set(this.editingOptional, id, false);
			} else {
				const defaultLimit =
					benefit.benefitLimit && benefit.benefitLimit.defaultLimit
						? Number(benefit.benefitLimit.defaultLimit)
						: null;
				const payload = Object.assign({}, benefit, {
					params: { limit: defaultLimit },
				});
				this.setBenefitsData(payload);
				this.$set(this.benefitInputs, id, defaultLimit);
			}
		},
		updateBenefitInput(benefitId) {
			const val = this.benefitInputs[benefitId];
			if (val === undefined) return;
			this.$set(this.benefitInputs, benefitId, Number(val));
		},
		startEditBenefit(benefit) {
			const id = benefit.benefitSystemRef;
			if (!this.benefitInputs[id]) {
				this.$set(
					this.benefitInputs,
					id,
					benefit.benefitLimit && benefit.benefitLimit.defaultLimit
						? Number(benefit.benefitLimit.defaultLimit)
						: null,
				);
			}
			this.$set(this.editingOptional, id, true);
		},
		saveInlineBenefit(benefit) {
			const id = benefit.benefitSystemRef;
			const val = this.benefitInputs[id];
			if (val === undefined || val === null || isNaN(Number(val))) {
				return;
			}
			const sel = this.selectedBenefits.find(function (b) {
				return b.benefitSystemRef === id;
			});
			const updated = sel
				? Object.assign({}, sel, {
						params: Object.assign({}, sel.params || {}, {
							limit: Number(val),
						}),
					})
				: { benefitSystemRef: id, params: { limit: Number(val) } };
			this.updateSelectedBenefit(updated);
			this.$set(this.editingOptional, id, false);
			const benefitData = {
				limit: val,
				benefit: benefit,
			};
			this.setBenefitsData(benefitData);
		},
		cancelInlineEdit(benefit) {
			const id = benefit.benefitSystemRef;
			const sel = this.selectedBenefits.find(function (b) {
				return b.benefitSystemRef === id;
			});
			if (sel && sel.params && sel.params.limit !== undefined) {
				this.$set(this.benefitInputs, id, sel.params.limit);
			} else {
				this.$set(
					this.benefitInputs,
					id,
					benefit.benefitLimit && benefit.benefitLimit.defaultLimit
						? Number(benefit.benefitLimit.defaultLimit)
						: null,
				);
			}
			this.$set(this.editingOptional, id, false);
		},

		addAndEditBenefit(benefit) {
			if (!benefit || !benefit.benefitSystemRef) return;
			const id = benefit.benefitSystemRef;
			if (!this.isBenefitSelected(id)) {
				const defaultLimit =
					benefit.benefitLimit && benefit.benefitLimit.defaultLimit
						? Number(benefit.benefitLimit.defaultLimit)
						: null;
				const payload = Object.assign({}, benefit, {
					params: { limit: defaultLimit },
				});
				this.setBenefitsData(payload);
				this.$set(this.benefitInputs, id, defaultLimit);
			}
			this.$set(this.editingOptional, id, true);
		},
		saveQuoteBenefit(benefit) {
			const isExisted = this.selectedBenefits.some(
				(ben) =>
					ben.benefit.benefitSystemRef == benefit.benefitSystemRef,
			);

			const id = benefit.benefitSystemRef;
			const val = this.coreInputs[id];

			// if (val === undefined || val === null || isNaN(Number(val))) return;

			this.$set(this.coreAdjustments, id, Number(val));
			this.$set(this.editingCore, id, false);

			const benefitData = {
				benefit,
				limit: Number(val) || benefit.benefitLimit.defaultLimit,
			};

			if (isExisted) {
				this.updateSelectedBenefit(benefitData);
			} else {
				this.setBenefitsData(benefitData);
			}
		},
		// Core editing handlers
		startEditCoreBenefit(benefit) {
			// if (!benefit || !benefit.benefitSystemRef) return;
			const id = benefit.benefitSystemRef;
			const current =
				this.adjustedCoreLimits[id] !== undefined
					? this.adjustedCoreLimits[id]
					: this.coreBenefitLimits[id] ||
						  (benefit.benefitLimit &&
								benefit.benefitLimit.defaultLimit)
						? Number(benefit.benefitLimit.defaultLimit)
						: null;
			this.$set(this.coreInputs, id, current);
			this.$set(this.editingCore, id, true);
		},

		// ------------------------------
		// EXCESSESS LOGIC
		// ------------------------------

		toggleExcessCheckbox(excess) {
			const isExisted = this.selectedExcesses.some(
				(e) => e.label == excess.label,
			);

			if (isExisted) {
				this.updateSelectedExcess(excess);
			} else {
				this.setSelectedExcess(excess);
			}
		},

		deleteExcess(excess) {
			return this.removeExcess(excess.label);
		},
		toggleLiabilityCheckbox(liability) {
			this.$set(
				this.liabilityEnabled,
				liability.id,
				!this.liabilityEnabled[liability.id],
			);
			if (
				this.liabilityEnabled[liability.id] &&
				(liability.amount === undefined || liability.amount === null)
			) {
				liability.amount =
					liability.defaultAmount || liability.amount || 0;
			}
		},

		onExcessCheckboxChange(excess) {
			if (!excess || !excess.id) return;
			const id = excess.id;
			const enabled = !!this.excessEnabled[id];
			this.$set(this.excessEnabled, id, !enabled);
			if (!enabled) {
				const defaultAmount =
					excess.amount !== undefined && excess.amount !== null
						? Number(excess.amount)
						: excess.defaultAmount || 0;
				this.$set(this.excessInputs, id, defaultAmount);
			} else {
				this.$set(this.editingExcess, id, false);
			}
		},
		startEditExcess(excess) {
			if (!excess || !excess.id) return;
			const id = excess.id;
			if (
				this.excessInputs[id] === undefined ||
				this.excessInputs[id] === null
			) {
				this.$set(
					this.excessInputs,
					id,
					Number(
						excess.amount !== undefined && excess.amount !== null
							? excess.amount
							: excess.defaultAmount || 0,
					),
				);
			}
			this.$set(this.editingExcess, id, true);
		},
		updateExcessInput(excessId) {
			if (this.excessInputs[excessId] === undefined) return;
			this.$set(
				this.excessInputs,
				excessId,
				Number(this.excessInputs[excessId]),
			);
		},
		saveInlineExcess(excess) {
			if (!excess || !excess.id) return;
			const id = excess.id;
			const val = this.excessInputs[id];
			if (val === undefined || val === null || isNaN(Number(val))) {
				return; // keep editor open
			}
			// find and update the mockExcesses entry
			const idx = this.mockExcesses.findIndex((e) => e.id === id);
			if (idx !== -1) {
				const updated = Object.assign({}, this.mockExcesses[idx], {
					amount: Number(val),
				});
				this.$set(this.mockExcesses, idx, updated);
			}
			this.$set(this.editingExcess, id, false);
			// ensure the checkbox is enabled
			this.$set(this.excessEnabled, id, true);
		},
		cancelInlineExcess(excess) {
			if (!excess || !excess.id) return;
			const id = excess.id;
			// reset input to current or default
			const current = this.mockExcesses.find((e) => e.id === id);
			if (current && current.amount !== undefined) {
				this.$set(this.excessInputs, id, Number(current.amount));
			} else {
				this.$set(
					this.excessInputs,
					id,
					Number(excess.defaultAmount || 0),
				);
			}
			this.$set(this.editingExcess, id, false);
		},
		addAndEditExcess(excess) {
			if (!excess || !excess.id) return;
			const id = excess.id;
			if (!this.excessEnabled[id]) {
				const defaultAmount =
					excess.amount !== undefined && excess.amount !== null
						? Number(excess.amount)
						: excess.defaultAmount || 0;
				this.$set(this.excessEnabled, id, true);
				this.$set(this.excessInputs, id, defaultAmount);
			}
			this.$set(this.editingExcess, id, true);
		},

		formatExcess(excess) {
			if (excess.rate.rateType == 'percentage') {
			}
		},

		// ------------------------------
		// LIABILITY LOGIC
		// ------------------------------
		onLiabilityCheckboxChange(liability) {
			if (!liability || !liability.name) return;
			const id = liability.name;
			const enabled = !!this.liabilityEnabled[id];
			this.$set(this.liabilityEnabled, id, !enabled);
			if (!enabled) {
				const defaultAmount =
					liabsaveCoreility.amount !== undefined &&
					liability.amount !== null
						? Number(liability.amount)
						: liability.defaultAmount || 0;
				this.$set(this.liabilityInputs, id, defaultAmount);
			} else {
				this.$set(this.editingLiability, id, false);
			}
		},
		startEditLiability(liability) {
			if (!liability || !liability.name) return;
			const id = liability.name;
			if (
				this.liabilityInputs[id] === undefined ||
				this.liabilityInputs[id] === null
			) {
				this.$set(
					this.liabilityInputs,
					id,
					Number(
						liability.amount !== undefined &&
							liability.amount !== null
							? liability.amount
							: liability.defaultAmount || 0,
					),
				);
			}
			this.$set(this.editingLiability, id, true);
		},
		updateLiabilityInput(liabilityId) {
			if (this.liabilityInputs[liabilityId] === undefined) return;
			this.$set(
				this.liabilityInputs,
				liabilityId,
				Number(this.liabilityInputs[liabilityId]),
			);
		},
		saveInlineLiability(liability) {
			if (!liability || !liability.name) return;
			const id = liability.name;
			const val = this.liabilityInputs[id];
			if (val === undefined || val === null || isNaN(Number(val))) return;
			// const idx = this.liabilities.findIndex((l) => l.name === id);
			// if (idx !== -1) {
			//   const updated = Object.assign({}, this.selectedLiabilities[idx], { amount: Number(val) });
			//   this.$set(this.liabilities, idx, updated);
			// }
			this.$set(this.editingLiability, id, false);
			this.$set(this.liabilityEnabled, id, true);
			const isExisted = this.selectedLiabilities.some(
				(l) => l.liability.name == liability.name,
			);
			const liabilityData = {
				limit: val,
				data: liability,
			};
			if (isExisted) {
				this.updateSelectedLiabilityData(liabilityData);
			} else {
				this.setLiabilityData(liabilityData);
			}
		},
		cancelInlineLiability(liability) {
			if (!liability || !liability.id) return;
			const id = liability.id;
			const current = this.mockLiabilities.find((e) => e.id === id);
			if (current && current.amount !== undefined) {
				this.$set(this.liabilityInputs, id, Number(current.amount));
			} else {
				this.$set(
					this.liabilityInputs,
					id,
					Number(liability.defaultAmount || liability.amount || 0),
				);
			}
			this.$set(this.editingLiability, id, false);
		},
		addAndEditLiability(liability) {
			if (!liability || !liability.id) return;
			const id = liability.id;
			if (!this.liabilityEnabled[id]) {
				const defaultAmount =
					liability.amount !== undefined && liability.amount !== null
						? Number(liability.amount)
						: liability.defaultAmount || liability.amount || 0;
				this.$set(this.liabilityEnabled, id, true);
				this.$set(this.liabilityInputs, id, defaultAmount);
			}
			this.$set(this.editingLiability, id, true);
		},
		async save() {
			this.startLoading();
			await new Promise(function (resolve) {
				setTimeout(resolve, 2000);
			});
			try {
				const res = await GetCmQuotation(this.quoteData());
				if (res.status) {
					console.log(res.data.result.data);
					this.$store.dispatch(
						'quotation/setCalculatedQuotation',
						res.data.result.data,
					);
					this.stopLoading();
					this.$router.push({ name: 'Quotation' });
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Quotation Retrieved',
						message:
							'Your quotation has been successfully retrieved.',
						duration: 3000,
					});
				}
			} catch (err) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Quotation Error',
					message:
						err.response &&
						err.response.data &&
						err.response.data.message
							? err.response.data.message
							: 'An error occurred while fetching the quotation. Please try again.',
					duration: 5000,
				});
			} finally {
				this.stopLoading();
			}
		},
		previous() {
			this.$router.push({ name: 'BasicCoverDetails' });
		},
		// -----
		// Quotation
		// ----
		quoteData() {
			return {
				productSystemRef: this.product.productSystemRef,
				risk: {
					value: this.selectQuotationData.vehicleValue,
				},
				additionalBenefits: this.selectedBenefits,
				coreAdjustments: this.coreAdjustments,
				excesses: this.mockExcesses,
				coverDetails: {
					period: Number(this.selectQuotationData.period),
				},
			};
		},
		formatKES(value) {
			if (value === undefined || value === null || value === '')
				return '-';
			const num = Number(value);
			if (isNaN(num)) return '-';
			return 'KES ' + num.toLocaleString();
		},
		displayCoreLimit(benefit) {
			const adjusted = this.adjustedCoreLimits[benefit.benefitSystemRef];
			const limit =
				adjusted !== undefined
					? adjusted
					: this.coreBenefitLimits[benefit.benefitSystemRef] ||
						benefit.benefitLimit.defaultLimit;
			return limit ? this.formatKES(limit) : '-';
		},
		findCoreName(key) {
			const benefit = this.embeddedBenefits.find(
				(b) => b.benefitSystemRef === key,
			);
			return benefit ? benefit.benefit_category_name : 'Unknown Benefit';
		},

		updateCoreInput(coreId) {
			if (this.coreInputs[coreId] === undefined) return;
			this.$set(this.coreInputs, coreId, Number(this.coreInputs[coreId]));
		},

		cancelCoreEdit(benefit) {
			if (!benefit || !benefit.benefitSystemRef) return;
			const id = benefit.benefitSystemRef;
			const current =
				this.adjustedCoreLimits[id] !== undefined
					? this.adjustedCoreLimits[id]
					: this.coreBenefitLimits[id] ||
						  (benefit.benefitLimit &&
								benefit.benefitLimit.defaultLimit)
						? Number(benefit.benefitLimit.defaultLimit)
						: null;
			this.$set(this.coreInputs, id, current);
			this.$set(this.editingCore, id, false);
		},
	},
	mounted() {
		this.selectedBenefits.forEach((b) => {
			if (
				b.benefitSystemRef &&
				b.params &&
				b.params.limit !== undefined &&
				b.params.limit !== null
			) {
				this.$set(
					this.benefitInputs,
					b.benefitSystemRef,
					Number(b.params.limit),
				);
			}
		});
		console.log(
			'selectedBenefits prefill count:',
			(this.selectedBenefits || []).length,
		);
	},
};
</script>
